import "./styles/SingleMenuConfiguration.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Button, {IconButton} from '@atlaskit/button/new';
import MenuBar from "../MenuBar";
import {navigationService} from "../../api/NavigationService";
import {Menu, MenuItem} from "navigation-common/types/navigation-types";
import {PAGE_ADMIN_MENU_OVERVIEW, PAGE_GENERAL_MENU_OVERVIEW} from "navigation-common/types/pages-constants";
import MenuItemAdmin from "./MenuItemAdmin";
import {navigationHelperService} from "../../api/NavigationHelperService";
import {useFlags} from '@atlaskit/flag';
import {errorFlag, successFlag} from "../../model/flags";
import Toggle from "@atlaskit/toggle";
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel'
import Rocket from "../../assets/img/rocket.svg";
import Tree, {moveItemOnTree, mutateTree, RenderItemParams, TreeData, TreeDestinationPosition, TreeItem, TreeSourcePosition} from "@atlaskit/tree";
import {ItemId} from "@atlaskit/tree/types";
import ConfigSidebar from "./ConfigSidebar";
import SettingsIcon from "@atlaskit/icon/glyph/settings";
import {treeHelperService} from "../../api/TreeHelperService";
import {SaveButton} from "./SaveButton";
import {confluenceService} from "../../api/ConfluenceService";
import {SelectOption} from "navigation-common/types/html-types";
import {AnalyticsEvents, postHogService} from "../../api/PostHogService";
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle'
import {AppLink} from "./AppLink";
import {useAsync, useEffectOnce} from "react-use";
import {ConfirmDialog} from "./ConfirmDialog";
import {ModalTransition} from "@atlaskit/modal-dialog";
import classNames from "classnames";
import {useParams} from "react-router-dom";
import {isAdminLocation} from "../../common/in-app-goto-handler";
import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import Lozenge from '@atlaskit/lozenge';
import {hasCqlFeature} from "../../common/FeatureFlags";
import Tooltip from '@atlaskit/tooltip';
import AdminImageModal from "./image-dialog/AdminImageModal";

function SingleMenuConfiguration() {
    const [menu, setMenu] = React.useState<Menu | undefined>(undefined);
    const [tree, setTree] = React.useState<TreeData | undefined>(undefined);
    const [isAutocompleteOpen, setIsAutocompleteOpen] = React.useState<boolean>(false);
    const [changeToggle, setChangeToggle] = React.useState(true);
    const [sidebarShown, setSidebarShown] = React.useState<boolean>(false);
    const [slideSidebar, setSlideSidebar] = React.useState<boolean>(false);
    const [newlyAddedItemKey, setNewlyAddedItemKey] = React.useState<string>("");
    const [anonymousSettingAvailable, setAnonymousSettingAvailable] = React.useState<boolean>(true);
    const [allSpaceLabels, setAllSpaceLabels] = React.useState<Array<SelectOption>>([]);
    const [unSavedChanges, setUnSavedChanges] = React.useState<boolean>(false);
    const [isDarkThemeActive, setIsDarkThemeActive] = React.useState<boolean>(false);
    const [hasDndStarted, setHasDndStarted] = React.useState<boolean>(false);
    const [hasCQLFeature, setHasCQLFeature] = React.useState<boolean>(true);
    const [imageModalMenuItem, setImageModalMenuItem] = useState<MenuItem | undefined>(undefined);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isSavingMenu, setIsSavingMenu] = useState(false);

    const openImageModal = useCallback((menuItem: MenuItem) => {
        setImageModalMenuItem(menuItem);
        setIsImageModalOpen(true);
    }, []);
    const closeImageModal = useCallback(() => {
        setImageModalMenuItem(undefined);
        setIsImageModalOpen(false)
    }, []);

    let addedItemRef: HTMLDivElement | null = null;
    const params = useParams();
    const {showFlag} = useFlags();
    const dndContainer = useRef<HTMLDivElement>(null);
    const treeRef = useRef<Tree>(null);
    const pageTopAnchor = useRef<HTMLDivElement>(null);

    const MAX_MENU_DEPTH = 2;
    AP.sizeToParent(true);

    useAsync(async () => {
        setHasCQLFeature(await hasCqlFeature());
    }, []);

    useAsync(async () => {
        if (!params.menuKey) {
            return;
        }
        await loadMenu(params.menuKey);
    }, []);

    useEffect(() => {
        resizeMenuContainer();
        confluenceService.getAllSpaceLabels().then((spaceLabels) => {
            setAllSpaceLabels(spaceLabels.map(label => ({value: label.id, label: label.name})));
        })
    }, []);

    useEffect(() => {
        if (dndContainer?.current?.clientHeight) {
            document.documentElement.style.setProperty("--dnd-container-height", `${dndContainer.current.clientHeight}px`);
        }
    }, [tree]);

    useEffect(() => {
        if (newlyAddedItemKey && addedItemRef) {
            addedItemRef.scrollIntoView({behavior: "smooth", block: "center"});
        }
    }, [newlyAddedItemKey]);

    useEffect(() => {
        isAnonymousSettingAvailable().then((isAvailable) => {
            setAnonymousSettingAvailable(isAvailable);
        });
    }, [menu]);

    const loadMenu = async (menuId: string) => {
        const menu = await navigationService.loadMenu(menuId);
        setMenu(menu);
        setTree(treeHelperService.transformMenuToTreeData(menu, undefined));
    }

    const saveMenu = async (updatedMenu: Menu) => {
        if (!menu) {
            return;
        }

        setIsSavingMenu(true);

        try {
            await navigationService.updateMenu(updatedMenu);
        } catch (e: any) {
            console.error("Update menu failed.", e);
            return handleOperationError(e);
        } finally {
            setIsSavingMenu(false);
        }

        setMenu(updatedMenu);
        showFlag(successFlag({title: "Menu saved"}));
        setUnSavedChanges(false);

        postHogService.logEvent(AnalyticsEvents.ADMIN_NAVI_SETTINGS_SAVE_CLICKED);
    }

    const onUndoConfirm = async () => {
        setUnSavedChanges(false);
        if (menu) {
            await loadMenu(menu.key);
            resizeMenuContainer();
        }
    }

    const handleOperationError = (error: any) => {
        const errMsg = error.response?.data ?? "There was an error. Please try again later.";
        showFlag(errorFlag({title: "Operation failed", description: errMsg}));
    }

    const isAnonymousSettingAvailable = async () => {
        const allMenus = await navigationService.loadAllMenus();
        let anonymousMenu: Menu | undefined = allMenus.find((m) => m.anonymousAccess === true);
        return anonymousMenu == undefined || anonymousMenu.key === menu?.key;
    };

    const menuItemChanged = () => {
        resizeMenuContainer();
        setTree(treeHelperService.transformMenuToTreeData(menu, tree));
        setNewlyAddedItemKey("");
        setUnSavedChanges(true);
    }

    const newMenuItemAdded = (insert: boolean, menuItem: MenuItem, newMenuItem?: MenuItem) => {
        resizeMenuContainer();
        if (insert) {
            navigationHelperService.insertMenuItemIntoList(menu, newMenuItem);
        }
        if (newMenuItem) {
            setNewlyAddedItemKey(newMenuItem.key);
        }
        if (tree) {
            const destinationItem = treeHelperService.getTreeItemByMenuItemKey(menuItem, tree);
            if (destinationItem) {
                destinationItem.isExpanded = true;
            }
        }

        setTree(treeHelperService.transformMenuToTreeData(menu, tree));
        setUnSavedChanges(true);
    }

    const menuItemDeleted = (menuItem: MenuItem) => {
        resizeMenuContainer();
        navigationHelperService.removeMenuItemsWithChildsFromList(menu, menuItem);
        setTree(treeHelperService.transformMenuToTreeData(menu, tree));
        setUnSavedChanges(true);
    }

    const addFirstMenuItem = () => {
        if (menu) {
            menu.menuItems.push(navigationHelperService.buildNewMenuItem("1.0.0", 1, "Your first menu item"));
            setTree(treeHelperService.transformMenuToTreeData(menu, tree));
            setUnSavedChanges(true);
        }
    }

    const getCollapseExpandIcon = (
        item: TreeItem,
        onExpand: (itemId: ItemId) => void,
        onCollapse: (itemId: ItemId) => void,
    ) => {
        if (item.children && item.children.length > 0) {
            return item.isExpanded ? (
                <IconButton appearance={"subtle"} icon={ChevronDownIcon} label={"Collapse"} onClick={() => onCollapse(item.id)}/>
            ) : (
                <IconButton appearance={"subtle"} icon={ChevronRightIcon} label={"Expand"} onClick={() => onExpand(item.id)}/>
            );
        }
        return "";
    };

    const draggableRenderItem = ({item, provided}: RenderItemParams) => {
        treeRef.current?.containerElement?.querySelectorAll(".drop-zone")
            .forEach((element: any) => element.classList.remove("drop-zone"));
        const combine = treeRef.current?.dragState?.combine;
        if (combine && tree?.items[combine.draggableId]?.data.level <= MAX_MENU_DEPTH) {
            treeRef.current?.itemsElement[combine.draggableId]?.querySelector(".nav-admin-group")?.classList.add("drop-zone");
        }

        return (
            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                <div className={"collapseBtnContainer"}>{getCollapseExpandIcon(item, onExpand, onCollapse)}</div>
                <div ref={(element) => {
                    if (item.data.key === newlyAddedItemKey) {
                        addedItemRef = element;
                    }
                }}>
                    <MenuItemAdmin
                        menuItem={item.data}
                        changeTrigger={menuItemChanged}
                        addNewMenuItemTrigger={newMenuItemAdded}
                        deleteMenuItemTrigger={menuItemDeleted}
                        changeToggle={false}
                        pageTopAnchor={pageTopAnchor.current}
                        newMenuItemKey={newlyAddedItemKey}
                        setIsAutocompleteOpen={setIsAutocompleteOpen}
                        hasCQLFeature={hasCQLFeature}
                        menuKey={menu?.key ?? ""}
                        openImageModal={openImageModal}
                    />
                </div>
            </div>
        );
    };

    function onDragStart() {
        setHasDndStarted(true);
    }

    const onDragEnd = (source: TreeSourcePosition, destination?: TreeDestinationPosition) => {
        setHasDndStarted(false);

        if (!destination || tree === undefined) {
            return;
        }

        const sourceItemId = tree.items[source.parentId].children[source.index];
        const sourceMenuItem = tree.items[sourceItemId].data as MenuItem;
        let destinationLevel = 1;

        if (destination.parentId !== tree.rootId) {
            const sourceDepth = treeHelperService.getMaxDepthOfTreeItemById({id: sourceItemId, tree});
            const destinationParentMenuItem = tree.items[destination.parentId].data as MenuItem;

            if ((destinationParentMenuItem.level + sourceDepth) > MAX_MENU_DEPTH) {
                const dismiss = showFlag(errorFlag({
                    title: "Cannot nest more than 3 levels deep"
                }));
                setTimeout(dismiss, 3000);
            }

            destinationLevel = destinationParentMenuItem.level + 1;
        }

        navigationHelperService.updateMenuItemLevelUnderneathMenuItem(sourceMenuItem, destinationLevel);

        const newTree = moveItemOnTree(tree, source, destination);
        newTree.items[destination.parentId].isExpanded = true;

        if (menu) {
            resizeMenuContainer();

            const newMenu = treeHelperService.transformTreeDataToMenu(menu, newTree);
            menu.menuItems = newMenu.menuItems;
            setTree(treeHelperService.transformMenuToTreeData(newMenu, newTree));
            setUnSavedChanges(true);
        }
    };

    const toggleTreeExpandState = (itemId: ItemId, expand: boolean) => {
        if (!tree || hasDndStarted) {
            return;
        }

        setTree(mutateTree(tree, itemId, {isExpanded: expand}));
        resizeMenuContainer();
    };

    const toggleTreeExpandAllState = (expand: boolean) => {
        if (!tree || hasDndStarted) {
            return;
        }

        let shallowTreeCopy = {...tree};
        const updateExpandState = (treeItem: TreeItem) => {
            shallowTreeCopy = mutateTree(shallowTreeCopy, treeItem.id, {isExpanded: expand});
            treeItem.children.forEach((childId) => {
                updateExpandState(shallowTreeCopy.items[childId]);
            });
        };

        updateExpandState(shallowTreeCopy.items[shallowTreeCopy.rootId]);

        setTree(shallowTreeCopy);
        resizeMenuContainer();
    }

    const onExpand = (itemId: ItemId) => {
        toggleTreeExpandState(itemId, true);
    };

    const onCollapse = (itemId: ItemId) => {
        toggleTreeExpandState(itemId, false);
    };

    const onExpandAll = () => {
        toggleTreeExpandAllState(true);
    };

    const onCollapseAll = () => {
        toggleTreeExpandAllState(false);
    };

    const openSidebar = () => {
        setSidebarShown(true);
        setSlideSidebar(true);
    }

    const closeSidebarTrigger = () => {
        setSlideSidebar(false);
        setTimeout(() => {
            setSidebarShown(false);
        }, 300);
    }

    const onThemeToggle = (darkTheme: boolean) => {
        setIsDarkThemeActive(darkTheme);
    }

    const colorChangeTrigger = (color: string, darkTheme: boolean) => {
        if (!menu) {
            setChangeToggle(!changeToggle);
            return;
        }

        if (darkTheme) {
            menu.primaryColorDarkTheme = color;
        } else {
            menu.primaryColor = color;
        }

        setChangeToggle(!changeToggle);
        setUnSavedChanges(true);
    }

    const subMenuBgColorChangeTrigger = (color: string, darkTheme: boolean) => {
        if (!menu) {
            setChangeToggle(!changeToggle);
            return;
        }

        if (darkTheme) {
            menu.subMenuBgColorDarkTheme = color;
        } else {
            menu.subMenuBgColor = color;
        }

        setUnSavedChanges(true);
        setChangeToggle(!changeToggle);
    }

    const groupsChangeTrigger = (groups: Array<string>) => {
        if (menu) {
            menu.userGroups = groups;
            setUnSavedChanges(true);
        }
    }

    const spaceLabelsChangeTrigger = (spaceLabels: Array<string>) => {
        if (menu) {
            menu.spaceLabels = spaceLabels;
            setUnSavedChanges(true);
        }
    };

    const activeChangeTrigger = (active: boolean) => {
        if (menu) {
            menu.active = active;
            setUnSavedChanges(true);
        }
        setChangeToggle(!changeToggle);
    }

    const anonymousAccessChangeTrigger = (active: boolean) => {
        if (menu) {
            menu.anonymousAccess = active;
            setUnSavedChanges(true);
        }
        setChangeToggle(!changeToggle);
    }

    const resizeMenuContainer = () => {
        document.documentElement.style.setProperty("--dnd-container-height", "undefined");
    }

    return <>
        {isImageModalOpen && imageModalMenuItem &&
            <AdminImageModal
                menuKey={menu!.key}
                menuItem={imageModalMenuItem}
                onModalClose={closeImageModal}
                changeTrigger={() => {
                    closeImageModal();
                    menuItemChanged();
                }}
            />
        }
        <div className={"ac-content configure-overview inner-content"} ref={pageTopAnchor}>
            <div className={"admin-top-line"}>
                <BackToOverviewBtn unSavedChanges={unSavedChanges}/>
                <span className={"admin-heading-feedback"}>
                    <a href={"https://seibert.biz/cloud-navigation-feedback"} target={"_blank"}>We ❤️&nbsp;Feedback</a>
                </span>
            </div>
            <div className={"admin-heading-wrapper"}>
                <h1 className="admin-heading">Configure menu &quot;{menu?.name}&quot;</h1>
            </div>
            <div className={"admin-sub-heading"}>
                <div className={"config-desc"}>
                    Configure a menu and help your users navigate confluence. Take a look at our <a
                    href={"https://seibert.biz/cloud-navigation-documentation"}
                    target={"_blank"}>documentation</a>, if you need help.
                </div>
            </div>

            <div className={"preview-wrapper"}>
                <div className={"preview-headline-wrapper"}>
                    <div className={"preview-headline"}>Your menu will look like this:</div>
                    <div className={"config-btn"}>
                        <Tooltip content="Configure menu settings">
                            <IconButton icon={SettingsIcon} label={"Setting icon"} appearance={"subtle"} onClick={openSidebar}/>
                        </Tooltip>
                    </div>
                </div>
                <div className={classNames("preview-inner", {"theme-dark": isDarkThemeActive, "theme-light": !isDarkThemeActive})}>
                    {menu?.menuItems && menu.menuItems.length > 0
                        ? <MenuBar menu={menu} preview={true} changeToggle={changeToggle}/>
                        : <div className={"empty-preview-state"}>No preview available</div>
                    }
                </div>
            </div>

            {menu?.menuItems && menu.menuItems.length > 0 && <>
                <div className={"headline structure"}>
                    <h4>Menu structure</h4>
                    <div className={"tree-actions"}>
                        <Button appearance={"default"} spacing={"compact"} onClick={onExpandAll}>Expand all</Button>
                        <Button appearance={"default"} spacing={"compact"} onClick={onCollapseAll}>Collapse all</Button>
                    </div>
                </div>
                <div className={"nav-admin-group tablehead"}>
                    <span className={"nav-admin-item level-id"}>ID</span>
                    <span className={"nav-admin-item icon"}>Icon</span>
                    <span className={"nav-admin-item name"}>Name</span>
                    <span className={"nav-admin-item cqlSwitch"}>CQL <span style={{marginLeft: "4px", marginTop: "-2px"}}><Lozenge
                        appearance="new">New</Lozenge></span></span>
                    <span className={"nav-admin-item link"}>Link</span>
                    <span className={"nav-admin-item newtab"}>New tab</span>
                    <span className={"nav-admin-item image"}>Image</span>
                    <span className={"nav-admin-item actions"}>Actions</span>
                </div>
            </>
            }

            {tree && <div className={classNames("tree-scroll-container", {"disable-overflow": isAutocompleteOpen})} ref={dndContainer}>
                <Tree
                    ref={treeRef}
                    tree={tree}
                    renderItem={draggableRenderItem}
                    onExpand={onExpand}
                    onCollapse={onCollapse}
                    onDragEnd={onDragEnd}
                    onDragStart={onDragStart}
                    isDragEnabled={true}
                    isNestingEnabled={true}
                    offsetPerLevel={32}
                />
            </div>
            }

            {/* empty state */}
            {menu && (!menu.menuItems || (menu.menuItems && menu.menuItems.length === 0)) &&
                <>
                    <div className={"headline structure"}>
                        <h4>Menu structure</h4>
                    </div>
                    <div className={"empty-state"}>
                        <img className={"empty-state-image"} src={Rocket} alt={"Rocket"}/>
                        <h3 className={"empty-state-title"}>Let’s get started</h3>
                        <div className={"empty-state-desc"}>It appears there are no menu items set for your
                            menu. <br/>Start by creating your first menu item.
                        </div>
                        <div className={"empty-state-button"}><Button appearance={"subtle"} onClick={() => addFirstMenuItem()}>Create a menu</Button></div>
                    </div>
                </>
            }
            {menu && <FooterActions menuIsActive={menu.active ?? false} onActiveChange={activeChangeTrigger}
                                    onSaveMenu={() => saveMenu(menu)}
                                    onUndoConfirm={onUndoConfirm}
                                    unSavedChanges={unSavedChanges}
                                    isSaving={isSavingMenu}/>
            }
        </div>
        {menu && sidebarShown && <ConfigSidebar
            editMenuMetaData={menu}
            saveMenuMetaData={saveMenu}
            closeTrigger={closeSidebarTrigger}
            openSidebar={slideSidebar}
            colorChangeTrigger={colorChangeTrigger}
            themeModeChangeTrigger={onThemeToggle}
            subMenuBgColorChangeTrigger={subMenuBgColorChangeTrigger}
            anonymousSettingDisabled={!anonymousSettingAvailable}
            groups={menu.userGroups ?? []}
            groupChangeTrigger={groupsChangeTrigger}
            spaceLabels={menu.spaceLabels ?? []}
            spaceLabelsChangeTrigger={spaceLabelsChangeTrigger}
            allSpaceLabels={allSpaceLabels}
            isDarkThemeMode={isDarkThemeActive}
            activeChangeTrigger={activeChangeTrigger}
            anonymousAccessChangeTrigger={anonymousAccessChangeTrigger}
        />}
    </>;
}

const BackToOverviewBtn = ({unSavedChanges}: { unSavedChanges: boolean }) => {
    const [isAdminPage, setIsAdminPage] = useState(false);

    useEffectOnce(() => {
        AP.navigator.getLocation((location: any) => {
            setIsAdminPage(isAdminLocation(location));
        });
    });

    return <div className={"back-to-overview"}>
        <AppLink
            to={isAdminPage ? PAGE_ADMIN_MENU_OVERVIEW.key : PAGE_GENERAL_MENU_OVERVIEW.key}
            hasIcon={true}
            askConfirmation={unSavedChanges}
            confirmMsg={"You have unsaved changes in your menu. If you leave now, these changes will be lost. Do you want to leave this page and discard your unsaved changes, or stay on this page to continue editing?"}
            confirmTitle={"Unsaved Changes"} confirmBtnText={"Leave Page"} cancelBtnText={"Stay on Page"}>
            <ArrowLeftCircleIcon label={"back"}/>
            <div>Menu Overview</div>
        </AppLink>
    </div>
}

function FooterActions({menuIsActive, onActiveChange, onSaveMenu, onUndoConfirm, unSavedChanges, isSaving}: Readonly<{
    menuIsActive: boolean,
    onActiveChange: (isActive: boolean) => void,
    onSaveMenu: () => Promise<void>,
    onUndoConfirm: () => Promise<void>,
    unSavedChanges: boolean,
    isSaving: boolean
}>) {
    const [showCancelConfirm, setShowCancelConfirm] = React.useState<boolean>(false);

    const onUndo = async () => {
        if (!unSavedChanges) {
            return;
        }
        setShowCancelConfirm(true);
    }

    const handleUndoConfirm = async () => {
        await onUndoConfirm();
        setShowCancelConfirm(false);
    }

    return (
        <div className={"footerWrapper"}>
            <div className={"footer"}>
                <div style={{flex: 1}}>
                    <BackToOverviewBtn unSavedChanges={unSavedChanges}/>
                </div>
                <div className={"menu-save-buttons"}>
                    <div className={"activate-menu-toggle"}>
                        <Toggle id={"navigation-show-menu"} isChecked={menuIsActive}
                                onChange={() => onActiveChange(!menuIsActive)}/>
                        <label htmlFor={"navigation-show-menu"}>Menu visibility</label>
                        <div className={"visibility-tooltip"}>
                            <Tooltip content={"Activate toggle to make the menu visible in Confluence"}>
                                <EditorPanelIcon label={""}/>
                            </Tooltip>
                        </div>
                    </div>
                    <div className={"cancel-btn"}>
                        <ModalTransition>{showCancelConfirm &&
                            <ConfirmDialog confirmTitle={"Confirm Undo Changes"}
                                           confirmMsg={"Are you sure you want to undo all unsaved changes? This action cannot be reversed, and all recent modifications will be lost."}
                                           confirmBtnText={"Yes, undo changes"}
                                           cancelBtnText={"Cancel"}
                                           onConfirm={handleUndoConfirm}
                                           onCancel={() => setShowCancelConfirm(false)}/>}
                        </ModalTransition>
                        <div style={{marginRight: "6px"}}><Button appearance={"subtle"} onClick={onUndo} isDisabled={!unSavedChanges}>Undo</Button></div>
                    </div>
                    <SaveButton onClick={onSaveMenu} btnTitle="Save" isLoading={isSaving}/>
                </div>
            </div>
        </div>
    );
}

export default SingleMenuConfiguration;
